import React, { useCallback, useMemo } from "react";
import { SearchOption, TypeaheadInput, AgGridReact, AgGrid } from "@jsq/ds/core";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { useGetPayeesForPayeeFilterLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { filterText } from "../styles_vanilla.css";

type PayeeEdgeType = {
  node: {
    id: string;
    name: string;
    payeeType: string;
    paymentPref: {
      bankInfoRedacted: string | null;
    };
  };
};

export const PayeeNameFilter = (filterParams: AgGridReact.CustomFilterProps) => {
  const { onModelChange, getValue } = filterParams;
  const [fetchPayeesQuery, { data, loading, error }] = useGetPayeesForPayeeFilterLazyQuery();

  useReportError(error ? formatError(error) : null);

  const handleSelection = useCallback(
    (value?: SearchOption) => {
      onModelChange(value?.label || null);
    },
    [onModelChange],
  );

  const payeeList = useMemo(
    () =>
      data?.payees?.edges.map((payee: PayeeEdgeType) => ({
        id: payee.node.id,
        label: payee.node.name,
      })) || [],
    [data],
  );

  const handleDebouncedSearch = useCallback(
    async (value?: string) => {
      if (value?.length) {
        await fetchPayeesQuery({
          context: clientContext(),
          variables: {
            filters: { searchTerm: value },
          },
          fetchPolicy: "no-cache",
        });
      } else {
        onModelChange(null);
      }
    },
    [fetchPayeesQuery, onModelChange],
  );

  // Note: doesFilterPass is only called with the Client-Side Row Model.
  const doesFilterPass = useCallback(
    (params: AgGrid.IDoesFilterPassParams) => {
      return getValue(params.node) == filterParams.model;
    },
    [getValue, filterParams],
  );

  // register filter callbacks with the grid
  AgGridReact.useGridFilter({ doesFilterPass });

  return (
    <TypeaheadInput
      className={filterText}
      data-testid="payee-name-filter"
      isClearable
      isFullWidth
      isLoading={loading}
      options={payeeList}
      onSelectOption={handleSelection}
      onDebouncedSearch={handleDebouncedSearch}
      placeholder="Search for a payee"
    />
  );
};
